import { randomBytes } from 'node:crypto';
import {
  createSessionDir,
  saveFrame,
  saveThumbnail,
  writeManifest,
  type SessionManifest,
  type ActionEntry,
} from './storage.js';

export interface RecordedAction {
  tool: string;
  args: Record<string, unknown>;
  screenshot: Buffer | null;
  pageUrl: string;
  pageTitle: string;
  screenshotFailed?: boolean;
}

/**
 * Manages the state of a single recording session.
 * Creates the session directory, tracks actions, and writes the manifest
 * after every action (atomic write).
 */
export class Session {
  readonly id: string;
  readonly startedAt: Date;
  private endedAt: Date | null = null;
  private readonly actions: ActionEntry[] = [];
  private readonly baseDir: string;
  private isFirstFrame = true;

  constructor(baseDir: string, id?: string) {
    this.id = id ?? Session.generateId();
    this.startedAt = new Date();
    this.baseDir = baseDir;

    createSessionDir(baseDir, this.id);
  }

  static generateId(): string {
    const timestamp = Date.now();
    const random = randomBytes(3).toString('hex'); // 6 hex chars
    return `bf-${timestamp}-${random}`;
  }

  get actionCount(): number {
    return this.actions.length;
  }

  get isEnded(): boolean {
    return this.endedAt !== null;
  }

  get duration(): number {
    const end = this.endedAt ?? new Date();
    return end.getTime() - this.startedAt.getTime();
  }

  /** Record an action with optional screenshot. Returns the action entry. */
  async recordAction(action: RecordedAction): Promise<ActionEntry> {
    const seq = this.actions.length + 1;
    let frameFilename = '';

    if (action.screenshot) {
      frameFilename = saveFrame(this.baseDir, this.id, seq, action.screenshot);

      // Save thumbnail from the first frame
      if (this.isFirstFrame) {
        this.isFirstFrame = false;
        try {
          await saveThumbnail(this.baseDir, this.id, action.screenshot);
        } catch (err) {
          console.error(
            `[browserforge] Thumbnail generation failed: ${err instanceof Error ? err.message : String(err)}`,
          );
        }
      }
    }

    const entry: ActionEntry = {
      seq,
      timestamp: new Date().toISOString(),
      tool: action.tool,
      args: action.args,
      frame: frameFilename,
      pageUrl: action.pageUrl,
      pageTitle: action.pageTitle,
      ...(action.screenshotFailed ? { screenshotFailed: true } : {}),
    };

    this.actions.push(entry);
    this.writeManifestToDisk();
    return entry;
  }

  /** End the session and write the final manifest. */
  end(): SessionManifest {
    this.endedAt = new Date();
    this.writeManifestToDisk();
    return this.toManifest();
  }

  toManifest(): SessionManifest {
    return {
      id: this.id,
      startedAt: this.startedAt.toISOString(),
      endedAt: this.endedAt?.toISOString() ?? null,
      duration: this.duration,
      actionCount: this.actions.length,
      actions: [...this.actions],
    };
  }

  private writeManifestToDisk(): void {
    writeManifest(this.baseDir, this.id, this.toManifest());
  }
}
